import {textMatchesArticleLanguage} from '@/service/articleLanguageFilter';

export type KeywordQuestionSource = string;

export type BriefQuestionRow = {
    question: string;
    source?: KeywordQuestionSource;
    aiSearchVolume?: number | null;
    answerHint?: string;
};

export function questionKeywordKey(text: string): string {
    return String(text || "")
        .replace(/[¿?？]/g, "")
        .replace(/[„“”"']/g, "")
        .replace(/^[^\p{L}\p{N}]+/u, "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
}

/** PAA / AI-chat / organic FAQ / autocomplete questions — not LSI entities. */
export function looksLikeQuestionPhrase(text: string): boolean {
    const value = String(text || "").replace(/\s+/g, " ").trim();
    if (!value) {
        return false;
    }
    if (/[?¿？]/.test(value)) {
        return true;
    }
    return /^(co |jak |pro[cč] |kdy |kde |kdo |kter |kolik |kam |[cč][ií]m |zda |je |jsou |k [cč]emu |na co |who |what |how |why |when |which |does |do |did |is |are |can |should |will |would |could )/i.test(value + " ");
}

export function partitionQuestionAndEntityPhrases(items: BriefQuestionRow[] = []): {
    questions: BriefQuestionRow[];
    entities: string[];
} {
    const questions: BriefQuestionRow[] = [];
    const entities: string[] = [];
    for (const item of items) {
        const text = String(item?.question || "").replace(/\s+/g, " ").trim();
        if (!text) {
            continue;
        }
        if (looksLikeQuestionPhrase(text)) {
            questions.push({...item, question: text});
        } else {
            entities.push(text);
        }
    }
    return {questions, entities};
}

export function phraseToKeywordRow(phrase: string, source?: KeywordQuestionSource): Record<string, unknown> {
    return {
        keyword: String(phrase || "").replace(/\s+/g, " ").trim(),
        keyword_info: {
            search_volume: 0,
            monthly_searches: [],
        },
        keyword_properties: {},
        briefSource: source,
    };
}

function asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === "object" ? value as Record<string, unknown> : {};
}

function googleVolumeOf(row: Record<string, unknown>): number {
    const info = asRecord(row.keyword_info);
    const monthly = Array.isArray(info.monthly_searches) ? info.monthly_searches : [];
    const monthlyAvg = monthly.length
        ? monthly.reduce((sum, item) => sum + (Number(asRecord(item).search_volume) || 0), 0) / monthly.length
        : 0;
    return Number(info.search_volume) || monthlyAvg || 0;
}

function aiVolumeOf(row: Record<string, unknown>): number {
    const info = asRecord(row.keyword_info);
    return Number(row.ai_search_volume)
        || Number(info.ai_search_volume)
        || Number(info.ai_monthly_searches)
        || Number(row.ai_monthly_searches)
        || 0;
}

function mergeKeywordQuestionRow(base: Record<string, unknown>, extra: Record<string, unknown>): Record<string, unknown> {
    const baseInfo = asRecord(base.keyword_info);
    const extraInfo = asRecord(extra.keyword_info);
    const baseProps = asRecord(base.keyword_properties);
    const extraProps = asRecord(extra.keyword_properties);
    const googleVolume = Math.max(googleVolumeOf(base), googleVolumeOf(extra));
    const aiVolume = Math.max(aiVolumeOf(base), aiVolumeOf(extra));
    const monthly = (Array.isArray(baseInfo.monthly_searches) && baseInfo.monthly_searches.length)
        ? baseInfo.monthly_searches
        : extraInfo.monthly_searches;
    return {
        ...base,
        ...extra,
        keyword: String(base.keyword || extra.keyword || ""),
        keyword_info: {
            ...baseInfo,
            ...extraInfo,
            search_volume: googleVolume || extraInfo.search_volume || baseInfo.search_volume || 0,
            monthly_searches: monthly,
            ai_search_volume: aiVolume || extraInfo.ai_search_volume || baseInfo.ai_search_volume || null,
        },
        keyword_properties: Object.keys(baseProps).length ? baseProps : extraProps,
        serp_info: extra.serp_info || base.serp_info,
        briefSource: extra.briefSource || base.briefSource,
        answerHint: extra.answerHint || base.answerHint,
        ai_search_volume: aiVolume || extra.ai_search_volume || base.ai_search_volume || null,
    };
}

export function questionToKeywordRow(question: BriefQuestionRow): Record<string, unknown> {
    const volume = question.aiSearchVolume || 0;
    return {
        keyword: question.question,
        keyword_info: {
            search_volume: 0,
            monthly_searches: [],
            ai_search_volume: volume || null,
        },
        keyword_properties: {},
        briefSource: question.source,
        answerHint: question.answerHint,
        ai_search_volume: volume || null,
    };
}

export function mergeLsiVariantRows(
    labsVariants: Array<Record<string, unknown>> | undefined,
    entities: string[] = []
): Array<Record<string, unknown>> {
    return mergeQuestionKeywordRows(
        labsVariants,
        entities
            .map((entity) => String(entity || "").replace(/\s+/g, " ").trim())
            .filter(Boolean)
            .map((entity) => ({question: entity, source: "lsi"}))
    );
}

export function mergeQuestionKeywordRows(
    labsQuestions: Array<Record<string, unknown>> | undefined,
    briefQuestions: BriefQuestionRow[] = [],
    languageCode?: string | null
): Array<Record<string, unknown>> {
    const rows: Array<Record<string, unknown>> = [];
    const indexByKey = new Map<string, number>();

    const upsert = (incoming: Record<string, unknown>) => {
        const keyword = String(incoming.keyword || "").replace(/\s+/g, " ").trim();
        const key = questionKeywordKey(keyword);
        if (!key || !textMatchesArticleLanguage(keyword, languageCode)) {
            return;
        }
        const existingIndex = indexByKey.get(key);
        if (existingIndex === undefined) {
            indexByKey.set(key, rows.length);
            rows.push({...incoming, keyword});
            return;
        }
        rows[existingIndex] = mergeKeywordQuestionRow(rows[existingIndex], {...incoming, keyword});
    };

    for (const row of labsQuestions || []) {
        upsert(row);
    }
    for (const question of briefQuestions || []) {
        upsert(questionToKeywordRow(question));
    }
    return rows;
}
